import {
  Alert,
  Box,
  CircularProgress,
  Container,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useAssignApprover, useUpdateUserRole, useUsers } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import type { Role } from '../types';
import PageHeader from '../components/ui/PageHeader';
import { glassCardSx } from '../styles/tokens';

const ROLES: Role[] = ['admin', 'user', 'viewer', 'approver'];

export default function UsersPage() {
  const { user } = useAuth();
  const users = useUsers();
  const updateRole = useUpdateUserRole();
  const assignApprover = useAssignApprover();

  if (user?.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Only administrators can manage users.</Alert>
      </Container>
    );
  }

  const approvers = (users.data ?? []).filter((u) => u.role === 'approver');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader
        title="User Management"
        subtitle="Assign roles and map approvers for record-deletion workflows."
      />

      {(updateRole.isError || assignApprover.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Update failed. Please try again.
        </Alert>
      )}

      {users.isLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : users.isError ? (
        <Alert severity="error">Failed to load users.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ ...glassCardSx, p: 0, overflow: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Assigned approver</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(users.data ?? []).map((u) => (
                <TableRow key={u.id} hover>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <FormControl size="small">
                      <Select
                        value={u.role}
                        onChange={(e) => updateRole.mutate({ id: u.id, role: e.target.value })}
                        disabled={u.id === user.id}
                      >
                        {ROLES.map((r) => (
                          <MenuItem key={r} value={r}>
                            {r}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    {u.role === 'user' ? (
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          displayEmpty
                          value={u.assigned_approver ?? ''}
                          onChange={(e) =>
                            assignApprover.mutate({
                              id: u.id,
                              approver_email: e.target.value ? String(e.target.value) : null,
                            })
                          }
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {approvers.map((a) => (
                            <MenuItem key={a.id} value={a.email}>
                              {a.email}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
}
